<template>
	<Teleport :to="teleportTo">
		<Transition name="dialog-fade" appear>
			<div
				 v-if="isVisible"
				 :class="[
					backdropClass,
					'fixed inset-0 z-50 block bg-black/70 overflow-y-auto',
					{
						'py-8': !isFullscreen,
						'flex items-center justify-center': centered,
					}
				]"
				 :id="dialogId"
				 role="dialog"
				 aria-modal="true"
				 :aria-labelledby="titleId"
				 :aria-describedby="bodyId"
				 @click.self="handleBackdropClick">

				<Transition name="dialog-slide" appear>
					<div ref="dialog"
						tabindex="-1"
						:class="[
							'app-dialog',
							dialogClass,
							'shadow-lg relative transition-all py-4 min-h-14',
							isFullscreen ? 'fixed inset-0 w-full h-full rounded-none overflow-y-auto' : 'rounded-md mx-auto max-w-screen-sm w-full',
							transparent ? 'bg-transparent' : 'bg-white',
						]">
						<!-- Header -->
						<template v-if="$scopedSlots.header || title">
							<div class="px-6 flex justify-between items-center">
								<slot name="header" :data="input$">
									<h3 :id="titleId" class="text-lg font-semibold text-gray-900 mb-0">{{ title }}
									</h3>
								</slot>

								<slot name="close-button" v-bind="{ hide }">
									<button @click="hide" aria-label="Close" class="text-gray-400 hover:text-gray-600">
										<i class="fas fa-close fa-xl"></i>
									</button>
								</slot>
							</div>
						</template>

						<template v-else>
							<slot name="close-button" v-bind="{ hide }">
								<button @click="hide" aria-label="Close" class="absolute z-20 top-3 right-4 text-gray-400 hover:text-gray-600">
									<i class="far fa-close fa-2xl"></i>
								</button>
							</slot>
						</template>

						<!-- Body -->
						<div :id="bodyId" :class="[
							'px-6',
							{
								'pt-8': isFullscreen && centered,
							},
							contentClass
						]">
							<slot :data="input$" />
						</div>

						<!-- Footer -->
						<div v-if="$slots.footer" class="px-6 py-4 border-t border-gray-200 text-right">
							<slot name="footer" :data="input$" />
						</div>
					</div>
				</Transition>
			</div>
		</Transition>
	</Teleport>
</template>

<script lang="ts">
import { PropType, computed, defineComponent } from "vue";
import Teleport from "vue2-teleport";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

let dialoginstance = 0;

const breakpoints = useBreakpoints(breakpointsBootstrapV5)
const activeBreakpoint = breakpoints.active()

type Breakpoint = "sm" | "md" | "lg";

export default defineComponent({
	name: "AppDialog",
	components: {
		Teleport
	},
	data() {
		dialoginstance++;
		const defaultId = `dialog-${dialoginstance}`;

		return {
			input$: null as any,
			isVisible: this.modelValue,
			dialogId: this.id || defaultId,
			titleId: `${this.id || defaultId}-title`,
			bodyId: `${this.id || defaultId}-body`,
			lastFocused: null as HTMLElement | null,
		};
	},
	props: {
		id: {
			type: String,
			default: ''
		},
		modelValue: {
			type: Boolean,
			default: false,
		},
		title: {
			type: String,
		},
		closeOnEsc: {
			type: Boolean,
			default: true,
		},
		closeOnBackdrop: {
			type: Boolean,
			default: true,
		},
		fullscreen: {
			type: [Boolean, Array, String] as PropType<boolean | (Breakpoint | string)[] | Breakpoint>,
			default: () => ['xs']
		},
		centered: {
			type: Boolean,
			default: false
		},
		backdropClass: {
			type: String,
		},
		dialogClass: {
			type: String,
		},
		contentClass: {
			type: String
		},
		transparent: {
			type: Boolean,
			default: false
		},
		teleportTo: {
			type: String,
			default: 'body'
		}
	},
	methods: {
		hide() {
			this.isVisible = false;
			this.$emit("update:modelValue", false);
			this.$emit("closed", this.dialogId);
		},
		show(data?: any) {
			this.input$ = data;
			this.isVisible = true;
			this.$emit("update:modelValue", true);
		},
		handleKeydown(e: KeyboardEvent) {
			if (e.key === "Escape" && this.isVisible) {
				this.hide();
			} else if (e.key === "Tab" && this.isVisible) {
				this.trapFocus(e);
			}
		},
		handleBackdropClick() {
			if (this.closeOnBackdrop) {
				this.hide();
			}
		},
		lockScroll(lock: boolean) {
			document.body.classList.toggle("overflow-hidden", lock);
		},
		activateFocusTrap() {
			this.lastFocused = document.activeElement as HTMLElement;
			this.$nextTick(() => {
				const dialogEl = this.$refs.dialog as HTMLElement;
				dialogEl?.focus();
			});
		},
		deactivateFocusTrap() {
			this.lastFocused?.focus();
		},
		trapFocus(e: KeyboardEvent) {
			const dialogEl = this.$refs.dialog as HTMLElement;
			if (!dialogEl) return;

			const focusable: NodeListOf<HTMLElement> = dialogEl.querySelectorAll(
				"a[href], area[href], input:not([disabled]), select:not([disabled]), " +
				"textarea:not([disabled]), button:not([disabled]), iframe, object, embed, " +
				'[tabindex="0"], [contenteditable]'
			);

			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					last.focus();
				}
			} else {
				if (document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		},
	},
	watch: {
		modelValue(val) {
			this.isVisible = val;
		},
		isVisible(val: boolean) {
			if (val !== this.modelValue) {
				this.$emit("update:modelValue", val);
			}

			this.lockScroll(val);

			if (val) {
				this.activateFocusTrap();
			} else {
				this.deactivateFocusTrap();
			}
		},
	},
	mounted() {
		if (this.closeOnEsc) {
			window.addEventListener("keydown", this.handleKeydown);
		}

		if (this.isVisible) {
			this.lockScroll(true);
			this.activateFocusTrap();
		}
	},
	beforeDestroy() {
		if (this.closeOnEsc) {
			window.removeEventListener("keydown", this.handleKeydown);
		}

		this.lockScroll(false);
		this.deactivateFocusTrap();
	},
	destroyed() {
		dialoginstance--;
	},
	setup({ fullscreen }) {
		const isFullscreen = computed<boolean>(() => {
			if (typeof fullscreen === 'boolean') return fullscreen;
			if (typeof fullscreen === 'string')
				return activeBreakpoint.value === fullscreen;
			return fullscreen.includes(activeBreakpoint.value ?? '');
		});

		return { isFullscreen, activeBreakpoint };
	}
});
</script>

<style scoped>
.dialog-fade-enter-active,
.dialog-fade-leave-active {
	transition: opacity 0.2s ease;
}

.dialog-fade-enter,
.dialog-fade-leave-to {
	opacity: 0;
}

.dialog-slide-enter-active,
.dialog-slide-leave-active {
	transition: transform 0.2s ease;
	transform: none;
}

.dialog-slide-enter,
.dialog-slide-leave-to {
	opacity: 0;
	transform: translateY(-30px);
}
</style>
